import { AlertModal } from "../../components/Modals/AlertModal/AlertModal";
import { Edit, Trash, More } from "@rsuite/icons";
import BlockRoundIcon from "@rsuite/icons/BlockRound";
import CheckRoundIcon from "@rsuite/icons/CheckRound";
import React, { useEffect, useRef, useState } from "react";
import { Popover, Dropdown, IconButton, Whisper } from "rsuite";
import ViewCustomerModal from "./ViewCustomer";
import { useDispatch } from "react-redux";
import { setActionData } from "../../../redux/userActionData";
import { useNavigate } from "react-router-dom";
import { statusArr } from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import { encryptUrl } from "../../../navigation/useDecryptParams";
import { ContentRoutes } from "../../../navigation/NavConstant";
import ViewOrdersModal from "../Orders/ViewOrdersModal";
import Followup from "../Followups/Followup";
import { isWebResolutionSize } from "../../../utils/helpers";

const CustomerSingleAction = ({
  customerData,
  onDeleteCustomer,
  onPublishCustomer,
  onFollowup,
  permission,
  ...props
}: {
  customerData: any;
  onDeleteCustomer: Function;
  onPublishCustomer: Function;
  onFollowup?: Function;
  permission: any;
}) => {
  const { t } = useTranslation();
  const customerRef = useRef({});
  const dispatch = useDispatch();
  const [openModalByName, setOpenModalByName] = useState("");
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openPublishModal, setOpenPublishModal] = useState(false);
  const [publishMessage, setPublishMessage] = useState("");
  const [isWebResolution, setIsWebResolution] = useState(isWebResolutionSize());
  const ecryptedCustomerId = encryptUrl(`${customerData.id}`);
  const orderFilterData = {
    customer: customerData.id,
    sort_by: "service_date",
    sort_order: "desc",
  };

  const customerUpdateLink = ContentRoutes.UPDATE_CUSTOMER.replace(
    ":id",
    ecryptedCustomerId.toString()
  );

  const customerFollowupLink = ContentRoutes.FOLLOWUPS.replace(
    ":id",
    ecryptedCustomerId.toString()
  );

  const navigate = useNavigate();
  const publishText =
    customerData.status == statusArr.active ? t("UNPUBLISH") : t("PUBLISH");
  const pMessage = t("CONFIRM_ACTION", {
    action:
      customerData.status == statusArr.active
        ? t("UNPUBLISH").toLowerCase()
        : t("PUBLISH").toLowerCase(),
    entity: t("CUSTOMER").toLowerCase(),
  });
  const publishIcon =
    customerData.status == statusArr.inactive ? (
      <BlockRoundIcon />
    ) : (
      <CheckRoundIcon />
    );

  const updateResolution = () => {
    setIsWebResolution(isWebResolutionSize());
  };

  useEffect(() => {
    window.addEventListener("resize", updateResolution);
  }, []);

  const renderMenu = (
    {
      onClose,
      left,
      top,
      className,
    }: { onClose: any; left: any; top: any; className: any },
    ref:
      | ((instance: HTMLDivElement | null) => void)
      | React.RefObject<HTMLDivElement>
      | null
      | undefined
  ) => {
    const handleSelect = (eventKey: any) => {
      const validPopupActions = [
        "view-customer",
        "view-customer-orders",
        "manage-follow-up",
        "view-customer-followups",
      ];
      if (eventKey == "view-customer-followups") {
        handleViewFollowupAction();
      } else if (eventKey == "edit-customer") {
        handleEditAction();
      } else if (eventKey == "delete-customer") {
        customerRef.current = customerData;
        setOpenDeleteModal(true);
      } else if (eventKey == "publish-customer") {
        customerRef.current = customerData;
        setOpenPublishModal(true);
        setPublishMessage(pMessage);
      } else if (validPopupActions.includes(eventKey.toLocaleLowerCase())) {
        setOpenModalByName(eventKey);
        dispatch(setActionData(customerData));
      }

      onClose();
    };
    return (
      <Popover ref={ref} className={className} style={{ left, top }} full>
        <Dropdown.Menu onSelect={handleSelect}>
          <Dropdown.Item eventKey={"view-customer"}>
            {t("VIEW")} {t("CUSTOMER")}
          </Dropdown.Item>
          <Dropdown.Item eventKey={"view-customer-orders"}>
            {t("VIEW")} {t("ORDERS")}
          </Dropdown.Item>
          <Dropdown.Item eventKey={"manage-follow-up"}>
            {t("CREATE")} {t("FOLLOWUP")}
          </Dropdown.Item>
          <Dropdown.Item eventKey={"view-customer-followups"}>
            {t("VIEW")} {t("FOLLOWUPS")}
          </Dropdown.Item>
          {!isWebResolution && (
            <>
              {permission.update && (
                <Dropdown.Item eventKey={"edit-customer"}>
                  {t("EDIT")} {t("CUSTOMER")}
                </Dropdown.Item>
              )}

              {permission.delete && (
                <Dropdown.Item eventKey={"delete-customer"}>
                  {t("DELETE")} {t("CUSTOMER")}
                </Dropdown.Item>
              )}

              {permission.publish && (
                <Dropdown.Item eventKey={"publish-customer"}>
                  {publishText} {t("CUSTOMER")}
                </Dropdown.Item>
              )}
            </>
          )}
        </Dropdown.Menu>
      </Popover>
    );
  };

  function handleEditAction() {
    navigate(`${customerUpdateLink}`);
  }

  function handleViewFollowupAction() {
    navigate(`${customerFollowupLink}`);
  }

  return (
    <div>
      {isWebResolution && (
        <>
          {permission.update && (
            <>
              <IconButton
                appearance="subtle"
                onClick={handleEditAction}
                title={t("EDIT")}
                icon={<Edit />}
              />
            </>
          )}
          {permission.delete && (
            <>
              <IconButton
                onClick={() => {
                  customerRef.current = customerData;
                  setOpenDeleteModal(true);
                }}
                icon={<Trash />}
                title={t("DELETE")}
                size="md"
              />
            </>
          )}
          {permission.publish && (
            <>
              <IconButton
                onClick={() => {
                  customerRef.current = customerData;
                  setOpenPublishModal(true);
                  setPublishMessage(pMessage);
                }}
                icon={publishIcon}
                title={publishText}
                size="md"
              />
            </>
          )}
        </>
      )}

      <Whisper
        placement="topEnd"
        trigger="click"
        speaker={renderMenu}
        preventOverflow={true}
      >
        <IconButton appearance="subtle" icon={<More />} />
      </Whisper>
      {openModalByName === "view-customer" && (
        <ViewCustomerModal
          status={openModalByName === "view-customer" ? true : false}
          toggle={setOpenModalByName}
        />
      )}
      {openModalByName === "view-customer-orders" && (
        <ViewOrdersModal
          orderFilterData={orderFilterData}
          openModal={openModalByName === "view-customer-orders" ? true : false}
          rowData={customerData}
          setOpenModal={setOpenModalByName}
        />
      )}
      {openModalByName === "manage-follow-up" && (
        <Followup
          openModal={openModalByName === "manage-follow-up" ? true : false}
          setOpenModal={setOpenModalByName}
          rowData={customerData}
          onFollowup={onFollowup}
        />
      )}
      <AlertModal
        open={openDeleteModal}
        handleClose={(e: any) => {
          if (e == true) {
            onDeleteCustomer(customerRef);
            setOpenDeleteModal(false);
          } else {
            setOpenDeleteModal(false);
          }
        }}
        displayText={t("CONFIRM_ACTION", {
          action: t("DELETE").toLowerCase(),
          entity: t("CUSTOMER").toLowerCase(),
        })}
        modalTitle={t("CONFIRM_ACTION_TITLE", {
          action: t("DELETE").toLowerCase(),
        })}
      />
      <AlertModal
        open={openPublishModal}
        handleClose={(e: any) => {
          if (e == true) {
            onPublishCustomer(customerRef);
            setOpenPublishModal(false);
          } else {
            setOpenPublishModal(false);
          }
        }}
        displayText={publishMessage}
      />
    </div>
  );
};

export default CustomerSingleAction;
